import React, { useState, useMemo } from "react";
import { useSkinsMasterContext } from "./SkinsMaster.jsx";
import { gets_skinurl, rarity_colors } from "./dependencies.jsx";
import { twMerge } from "tailwind-merge";
import { TokenIcon } from "../../components/utilityComps.jsx";
import { StorePackDetails } from "./StorePackDetails.jsx";
import { StoreItemSkeleton } from "./StoreItemSkeleton.jsx";

export const StorePacksContext = React.createContext({});
export const useStorePacksContext = () => React.useContext(StorePacksContext);

export const StorePacks = () => {
  const { store_packs, store_indis, qo_skin_instore } = useSkinsMasterContext();
  const is_loading = qo_skin_instore?.isLoading || !store_packs;

  const [search_text, set_search_text] = useState("");
  const [selected_pack, set_selected_pack] = useState(null);

  // Filter packs based on search
  const filtered_packs = useMemo(() => {
    if (!store_packs) return [];

    let packs = store_packs.filter(pack => pack.active);

    // Filter by search text
    if (search_text.length >= 2) {
      const search_lower = search_text.toLowerCase();
      packs = packs.filter(pack =>
        pack.name.toLowerCase().includes(search_lower) ||
        pack.id.toLowerCase().includes(search_lower)
      );
    }

    return packs;
  }, [store_packs, search_text]);

  // Calculate pack savings
  const calculate_pack_savings = (pack) => {
    if (!pack.override_price || !store_indis) return 0;

    const individual_total = pack.skinindi_ids.reduce((total, id) => {
      const individual_item = store_indis.find(item => item.id === id);
      return total + (individual_item?.price_sgc || 0);
    }, 0);

    const pack_price = pack.price_sgc;
    return individual_total - pack_price;
  };

  // Create store_indis map for faster lookups
  const store_indis_map = useMemo(() => {
    if (!store_indis) return {};
    const map = {};
    store_indis.forEach(item => {
      map[item.id] = item;
    });
    return map;
  }, [store_indis]);

  // Get pack display image from first skin
  const get_pack_image = (pack) => {
    const first_skin_id = pack.skinindi_ids[0];
    const first_skin = store_indis_map[first_skin_id];
    if (first_skin) {
      return gets_skinurl(first_skin.name, first_skin.rvmode, true);
    }
    return null;
  };

  // Get skin info helper
  const get_skin_info = (skin_id) => {
    const individual_item = store_indis_map[skin_id];
    return {
      rvmode: individual_item?.rvmode || skin_id.split("-")[0],
      rarity: individual_item?.rarity || "common",
    };
  };

  const store_packs_con = {
    search_text,
    set_search_text,
    selected_pack,
    set_selected_pack,
    filtered_packs,
    store_indis_map,
    get_pack_image,
    calculate_pack_savings,
  };

  return (
    <StorePacksContext.Provider value={store_packs_con}>
      <div className="w-full">
        {/* Show Details if pack is selected */}
        {selected_pack ? (
          <StorePackDetails />
        ) : (
          <div className="space-y-4 sm:space-y-6">
            {/* Search Bar */}
            <div className="max-w-md mx-auto">
              <input
                type="text"
                value={search_text}
                onChange={(e) => set_search_text(e.target.value)}
                placeholder="Search packs by name or ID..."
                className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base bg-slate-800 border border-slate-600 rounded-lg text-slate-100 placeholder-slate-400 focus:outline-none focus:border-r3reg focus:ring-1 focus:ring-r3reg/30 transition-all duration-200"
              />
              {search_text.length > 0 && search_text.length < 2 && (
                <p className="text-xs text-slate-400 mt-1">Type at least 2 characters to search</p>
              )}
            </div>

            {/* Results Count - Only show when actively searching */}
            {search_text.length >= 2 && (
              <div className="text-center text-gray-400 text-xs sm:text-sm">
                Showing {filtered_packs.length} skin pack{filtered_packs.length !== 1 ? 's' : ''}
              </div>
            )}

      {/* Packs Grid */}
      {is_loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-6">
          {Array.from({ length: 6 }).map((_, idx) => (
            <StoreItemSkeleton key={idx} />
          ))}
        </div>
      ) : filtered_packs.length === 0 ? (
        <div className="text-center text-gray-400 py-6 sm:py-8 text-sm sm:text-base">
          <p>No packs match the selected filters.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-6">
          {filtered_packs.map((pack) => {
            const pack_image = get_pack_image(pack);
            const savings = calculate_pack_savings(pack);

            return (
              <div
                key={pack.id}
                className="bg-r3dark/20 border border-r3lig/30 rounded-xl overflow-hidden hover:border-r3lig/50 transition-all duration-200"
              >
                {/* Pack Image */}
                <div className="aspect-[1166/656] relative overflow-hidden bg-gradient-to-br from-r3dark/40 to-r3lig/20">
                  {pack_image ? (
                    <img
                      src={pack_image}
                      alt={pack.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <span className="text-gray-400 text-lg font-medium">No Image</span>
                    </div>
                  )}

                  {/* Pack Badge */}
                  <div className="absolute top-3 left-3">
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-600 text-white">
                      PACK
                    </span>
                  </div>

                  {/* Savings Badge */}
                  {savings > 0 && (
                    <div className="absolute top-3 right-3">
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-600 text-white">
                        Save {savings} SGC
                      </span>
                    </div>
                  )}

                  {/* Pack Size Overlay */}
                  <div className="absolute bottom-3 left-3">
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-black/60 text-white">
                      Pack of {pack.skinindi_ids.length} skins
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-3 sm:p-4">
                  <h4 className="text-base sm:text-lg font-digi text-white mb-2 truncate">
                    {pack.name} Pack
                  </h4>

                  {/* Price */}
                  <div className="mb-3 sm:mb-4 flex flex-col sm:flex-row justify-center items-center gap-2 sm:gap-4">
                    {/* USD Pricing (WETH/DEZ) */}
                    <div className="flex items-center gap-1.5 sm:gap-2">
                      <div className="flex -space-x-1">
                        <TokenIcon token="WETH" size="md" className="z-10" />
                        <TokenIcon token="DEZ" size="md" className="z-0" />
                      </div>
                      <span className="text-sm sm:text-base font-bold text-white">
                        ${pack.price_usd}
                      </span>
                    </div>

                    {/* OR Divider */}
                    <div className="hidden sm:flex relative items-center justify-center w-12 h-6">
                      <div className="absolute top-1 left-0 w-5 h-[1px] bg-gray-400 transform rotate-45 origin-left"></div>
                      <div className="absolute bottom-1 left-0 w-5 h-[1px] bg-gray-400 transform rotate-45 origin-left"></div>
                      <div className="text-sm text-gray-300 font-medium">OR</div>
                    </div>
                    <div className="sm:hidden text-xs text-gray-400">or</div>

                    {/* SGC Pricing */}
                    <div className="flex items-center gap-1.5 sm:gap-2">
                      <TokenIcon token="SGC" size="md" />
                      <span className="text-sm sm:text-base font-bold text-r3reg">
                        {pack.price_sgc} SGC
                      </span>
                    </div>
                  </div>

                  {/* View Details Button */}
                  <button
                    onClick={() => set_selected_pack(pack)}
                    className="w-full py-2.5 sm:py-3 px-3 sm:px-4 rounded-lg text-sm sm:text-base font-medium transition-all duration-200 bg-r3reg hover:bg-r3dark text-white shadow-lg"
                  >
                    View Details
                  </button>
                </div>
              </div>
            );
          })}
            </div>
          )}
          </div>
        )}
      </div>
    </StorePacksContext.Provider>
  );
};